import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import throttle from 'lodash.throttle';
import { ThemeProvider } from 'styled-components';
import { getColors, getFonts, getBackground } from 'store/settings/selectors';
import {
  INDEX_PATH,
  SECTIONS_PATH,
  SECTION_PATH,
  RESULTS_PATH,
  SIGNUP_PATH,
  LOGIN_PATH,
  NOACCESS_PATH
} from 'constants/navigation';
import { withRouter, Switch, Route } from 'react-router-dom';
import Sections from 'pages/sections';
import SignUp from 'pages/signUp';
import Login from 'pages/login';
import NoAccess from 'pages/noAccess';
import CourseOverview from 'pages/courseOverview';
import * as actions from 'store/app/actions';
import { isAppLoading, isAppLoadingFailed } from 'store/app/selectors';
import withScrollOnRedirect from 'components/hocs/withScrollOnRedirect';
import NotFoundPage from 'pages/notFound/NotFoundPage';
import { getTreeOfContentVisibility } from 'store/treeOfContent/selectors';
import { isAuthenticated } from 'store/user/selectors';
import { isCourseAccessLimited } from 'store/course/selectors';
import { CircleLoader, PrivateRoute, RouteWithRedirect } from '../common';
import Layout from './Layout';
import RouteTransition from './RouteTransition';

export class Shell extends Component {
  state = {
    hasError: false
  };

  windowResizeHandler = throttle(() => {
    this.props.actions.resolutionChanged();
  }, 500);

  componentWillUnmount() {
    window.removeEventListener('resize', this.windowResizeHandler);
  }

  componentDidMount() {
    window.addEventListener('resize', this.windowResizeHandler);
    this.props.actions.resolutionChanged();
    this.props.actions.load();
  }

  componentDidCatch() {
    this.setState({ hasError: true });
  }

  render() {
    const { isLoading, isLoadingFailed } = this.props;
    const { hasError } = this.state;

    if (isLoading) {
      return <CircleLoader iconSize={96} />;
    }

    if (isLoadingFailed || hasError) {
      return <h1>Something went wrong.</h1>;
    }

    return this.renderRoute();
  }

  renderRoute() {
    const {
      isLowResolution,
      colors,
      fonts,
      isTocExpanded,
      background,
      isUserAuthenticated,
      userAccessIsLimited
    } = this.props;

    const forbidRoute = userAccessIsLimited || !isUserAuthenticated;
    const pathForPrivateRoute =
      userAccessIsLimited && isUserAuthenticated ? NOACCESS_PATH : LOGIN_PATH;

    return (
      <Route
        render={({ location }) => (
          <ThemeProvider theme={{ colors, fonts }}>
            <RouteTransition
              location={location}
              isLowResolution={isLowResolution}
              isTocExpanded={isTocExpanded}
              background={background}
            >
              <Layout className="layout">
                <Switch location={location}>
                  <PrivateRoute
                    exact
                    path={INDEX_PATH}
                    component={Sections}
                    forbidRoute={forbidRoute}
                    routeTo={pathForPrivateRoute}
                  />
                  <PrivateRoute
                    exact
                    path={SECTIONS_PATH}
                    component={Sections}
                    forbidRoute={forbidRoute}
                    routeTo={pathForPrivateRoute}
                  />
                  <PrivateRoute
                    exact
                    path={RESULTS_PATH}
                    component={CourseOverview}
                    forbidRoute={forbidRoute}
                    routeTo={pathForPrivateRoute}
                  />
                  <PrivateRoute
                    path={SECTION_PATH}
                    component={CourseOverview}
                    forbidRoute={forbidRoute}
                    routeTo={pathForPrivateRoute}
                  />
                  <RouteWithRedirect
                    exact
                    path={LOGIN_PATH}
                    shouldRedirect={!forbidRoute && isUserAuthenticated}
                    component={Login}
                  />
                  <RouteWithRedirect
                    exact
                    path={SIGNUP_PATH}
                    shouldRedirect={!forbidRoute && isUserAuthenticated}
                    component={SignUp}
                  />
                  <Route exact path={NOACCESS_PATH} component={NoAccess} />
                  <Route component={NotFoundPage} />
                </Switch>
              </Layout>
            </RouteTransition>
          </ThemeProvider>
        )}
      />
    );
  }

  static propTypes = {
    colors: PropTypes.object,
    fonts: PropTypes.object,
    background: PropTypes.object,
    actions: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isLoadingFailed: PropTypes.bool.isRequired,
    isLowResolution: PropTypes.bool.isRequired,
    isTocExpanded: PropTypes.bool.isRequired,
    isUserAuthenticated: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    userAccessIsLimited: PropTypes.bool
  };
}

function mapStateToProps(state) {
  return {
    colors: getColors(state),
    fonts: getFonts(state),
    isLoading: isAppLoading(state),
    isLoadingFailed: isAppLoadingFailed(state),
    isLowResolution: state.app.isLowResolution,
    isTocExpanded: getTreeOfContentVisibility(state),
    background: getBackground(state),
    isUserAuthenticated: isAuthenticated(state),
    userAccessIsLimited: isCourseAccessLimited(state)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(actions, dispatch)
  };
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withScrollOnRedirect(Shell))
);
